import { useEffect, useMemo, useRef } from 'react'

import { type TeamsLatestLocationContextValue } from '@/model/contexts/TeamsLatestLocationProvider.tsx'
import { type RegionSnapshotData, useRegion } from '@/model/contexts/useRegion.ts'
import { type Team, useTeams } from '@/model/contexts/useTeams.ts'
import { useTeamsLatestLocation } from '@/model/contexts/useTeamsLatestLocation.ts'
import { type TransportRequest, useTransportRequests } from '@/model/contexts/useTransportRequests.ts'

const computeRegionBounds = (
  transportRequests: ReadonlyArray<TransportRequest>,
  teams: ReadonlyArray<Team>,
  mapConfiguration?: RegionSnapshotData['mapConfiguration'],
  teamsLatestLocation: TeamsLatestLocationContextValue = {}
) => {
  const transportRequestsGeoPoints = transportRequests.flatMap(({ origin, destination, status }) =>
    ['pending', 'allocated', 'assigned', 'on_board'].includes(status)
      ? [origin.geolocation, destination.geolocation]
      : []
  )
  const teamGeoPoints = teams.flatMap(({ _id }) =>
    teamsLatestLocation[_id] ? [teamsLatestLocation[_id].geolocation] : []
  )
  const geoPoints = [...transportRequestsGeoPoints, ...teamGeoPoints]
  if (!mapConfiguration && geoPoints.length === 0) {
    // Default to Eastern Canada bounds (Ontario + Quebec)
    const southWest = { lat: 42.5, lng: -82 } // SW Ontario
    const northEast = { lat: 50, lng: -64 } // NE Quebec
    return new google.maps.LatLngBounds(southWest, northEast)
  }

  return geoPoints.reduce(
    (bounds, point) => bounds.extend(point),
    mapConfiguration
      ? new google.maps.LatLngBounds(mapConfiguration.ne, mapConfiguration.sw)
      : new google.maps.LatLngBounds()
  )
}

const computeTransportRequestBounds = (
  transportRequest: TransportRequest,
  teamsLatestLocation: TeamsLatestLocationContextValue = {}
) => {
  const bounds = new google.maps.LatLngBounds()
  bounds.extend(transportRequest.origin.geolocation)
  bounds.extend(transportRequest.destination.geolocation)
  if (transportRequest.team?._id && teamsLatestLocation[transportRequest.team._id]) {
    bounds.extend(teamsLatestLocation[transportRequest.team._id].geolocation)
  }
  return bounds
}

export const useAppMapBounds = () => {
  const { region } = useRegion()
  const { transportRequests } = useTransportRequests()
  const { teams } = useTeams()
  const teamsLatestLocation = useTeamsLatestLocation()
  const teamsLatestLocationRef = useRef<TeamsLatestLocationContextValue | undefined>(undefined)

  useEffect(() => {
    teamsLatestLocationRef.current = teamsLatestLocation
  }, [teamsLatestLocation])

  return useMemo(
    () => ({
      computeRegionBounds: () =>
        computeRegionBounds(
          transportRequests,
          teams.filter((t) => t.status !== 'DISSOLVED'),
          region.mapConfiguration,
          teamsLatestLocationRef.current
        ),
      computeTransportRequestBounds: (request: TransportRequest) =>
        computeTransportRequestBounds(request, teamsLatestLocationRef.current),
    }),
    [region?.mapConfiguration, teams, transportRequests]
  )
}
